/**
 * Route / goto alarm checks, pulled into one pure place so the on-screen banner,
 * the audible siren and the background (screen-off) evaluator all agree on when
 * a waypoint arrival or an off-track excursion is firing.
 *
 * Before this the route/XTD alarms were worked out only as banner strings in the
 * Shell, so they never reached anyAlarmActive: the siren and haptics stayed
 * silent on them, and the background path ignored them entirely. Same logic as
 * the Shell used, moved here and shared.
 *
 * Pure and clock-free, so it's easy to test and safe to run on a background fix.
 */
import { rhumbDistanceNm } from '@/nav';
import {
  monitorRoute,
  followLeg,
  clampLegIndex,
  APPROACH_LEG_INDEX,
  effectiveLegXtd,
  effectiveWaypointRadius,
  type Route,
  type Waypoint,
} from '@/routing';

export interface RouteAlarmInputs {
  /** Own ship now, [lon, lat]. */
  position: [number, number];
  routes: Route[];
  marks: Waypoint[];
  followRouteId: string | null;
  followFromLegIndex: number | null;
  /**
   * The authoritative active leg when a route is activated for follow. When set,
   * the off-track check measures against THIS leg (the one we're steering) rather
   * than re-picking the nearest one, so the alarm and the HUD agree on the leg.
   */
  activeLegIndex?: number | null;
  /** The selected route, so its waypoint arrivals count even before we follow. */
  activeRouteId?: string | null;
}

export interface RouteAlarmResult {
  /** A waypoint or mark whose arrival radius the boat is now inside. */
  waypointArrival: { label: string } | null;
  /** The followed leg's cross-track limit is broken (off-track), signed xte. */
  offTrack: { xteNm: number; xtdNm: number } | null;
}

/** Work out which route/goto alarms are firing right now. */
export function evaluateRouteAlarms(i: RouteAlarmInputs): RouteAlarmResult {
  let waypointArrival: RouteAlarmResult['waypointArrival'] = null;

  // Standalone marks that have an arrival radius set.
  for (const m of i.marks) {
    if (
      typeof m.arrivalRadiusNm === 'number' &&
      rhumbDistanceNm(i.position, m.position) <= m.arrivalRadiusNm
    ) {
      waypointArrival = { label: m.name || 'mark' };
      break;
    }
  }

  // Route waypoints (route-wide radius, or a per-waypoint override). Only the
  // route we're following, or the one selected, is watched: a waypoint on some
  // other stored route in the same water shouldn't sound an arrival we're not
  // navigating to.
  if (!waypointArrival) {
    for (const r of i.routes) {
      if (r.id !== i.followRouteId && r.id !== i.activeRouteId) continue;
      for (const w of r.waypoints) {
        const rad = effectiveWaypointRadius(r, w);
        if (rad != null && rhumbDistanceNm(i.position, w.position) <= rad) {
          waypointArrival = { label: w.name || 'WP' };
          break;
        }
      }
      if (waypointArrival) break;
    }
  }

  // Off-track: only against the route we're actively following, and against the
  // leg we're steering. Prefer the stored active leg (activated follow); fall
  // back to the nearest-leg pick when there's no stored index.
  let offTrack: RouteAlarmResult['offTrack'] = null;
  if (i.followRouteId) {
    const route = i.routes.find((r) => r.id === i.followRouteId);
    // The approach leg to WP1 has no planned track (it runs from wherever we are to
    // the mark, redrawn each fix), so there is nothing to be off and no alarm to
    // raise, by either route below. Real legs carry on as before.
    if (route && route.waypoints.length >= 2 && i.activeLegIndex !== APPROACH_LEG_INDEX) {
      if (i.activeLegIndex != null) {
        const idx = clampLegIndex(route, i.activeLegIndex);
        const f = followLeg(route, i.position, idx);
        if (f) {
          const xtd = effectiveLegXtd(route, f.fromId);
          if (xtd != null && Math.abs(f.xteNm) > xtd) {
            offTrack = { xteNm: f.xteNm, xtdNm: xtd };
          }
        }
      } else {
        const mon = monitorRoute(route, i.position, i.followFromLegIndex ?? 0);
        if (mon.activeLegIndex != null && mon.active) {
          const fromId = route.waypoints[mon.activeLegIndex].id;
          const xtd = effectiveLegXtd(route, fromId);
          if (xtd != null && Math.abs(mon.active.xteNm) > xtd) {
            offTrack = { xteNm: mon.active.xteNm, xtdNm: xtd };
          }
        }
      }
    }
  }

  return { waypointArrival, offTrack };
}
